import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
    Search,
    CheckCircle,
    XCircle,
    Filter,
    Eye,
    X,
    FileText,
    KeyRound,
    Copy,
    Mail,
    CreditCard,
    BadgeCheck,
    AlertTriangle,
    Download,
} from 'lucide-react';
import adhesionsService, { Adhesion, AdhesionStatistics } from '../../services/adhesions.service';
import { BASE_URL, getFileUrl } from '../../services/api';
import * as XLSX from 'xlsx';

interface GeneratedCredentials {
    code: string;
    prenom: string;
    nom: string;
    email: string;
    numeroMembre: string;
    adhesionId: string;
}

const AdhesionListPage: React.FC = () => {
    const location = useLocation();

    const [adhesions, setAdhesions] = useState<Adhesion[]>([]);
    const [loading, setLoading] = useState(true);
    const [stats, setStats] = useState<AdhesionStatistics | null>(null);
    const [filterStatut, setFilterStatut] = useState<string>('');
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [selectedAdhesion, setSelectedAdhesion] = useState<Adhesion | null>(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);

    // Payment confirmation modal
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [adhesionToValidate, setAdhesionToValidate] = useState<Adhesion | null>(null);

    // Credentials modal (shown after validation)
    const [generatedCredentials, setGeneratedCredentials] = useState<GeneratedCredentials | null>(null);
    const [emailSent, setEmailSent] = useState(false);

    // Stats fetching
    useEffect(() => {
        fetchStats();
    }, [location.state?.refresh]);

    // Data fetching with debounce
    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchAdhesions();
        }, 300);
        return () => clearTimeout(delayDebounceFn);
    }, [page, filterStatut, searchTerm, location.state?.refresh]);

    const fetchStats = async () => {
        try {
            const data = await adhesionsService.getStatistics();
            setStats(data);
        } catch (error) {
            console.error('Error fetching stats:', error);
        }
    };

    const fetchAdhesions = async () => {
        setLoading(true);
        try {
            const response = await adhesionsService.getAll(filterStatut, page, 10, searchTerm);
            setAdhesions(response.data);
            setTotalPages(response.totalPages);
        } catch (error) {
            console.error('Error fetching adhesions:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleApprove = async (id: string) => {
        if (!window.confirm('Voulez-vous vraiment approuver cette demande ?')) return;

        setActionLoading(true);
        try {
            await adhesionsService.approve(id);
            setIsModalOpen(false);
            fetchAdhesions();
            fetchStats();
        } catch (error: any) {
            // eslint-disable-next-line
            alert(error.response?.data?.message || 'Erreur lors de l\'approbation');
        } finally {
            setActionLoading(false);
        }
    };

    const handleReject = async (id: string) => {
        const reason = window.prompt('Motif du rejet (optionnel):');
        if (reason === null) return; // Cancelled

        setActionLoading(true);
        try {
            await adhesionsService.reject(id, reason);
            setIsModalOpen(false);
            fetchAdhesions();
            fetchStats();
        } catch (error: any) {
            // eslint-disable-next-line
            alert(error.response?.data?.message || 'Erreur lors du rejet');
        } finally {
            setActionLoading(false);
        }
    };

    /** Step 1: open payment confirmation modal */
    const openPaymentConfirmation = (adhesion: Adhesion) => {
        setAdhesionToValidate(adhesion);
        setIsPaymentModalOpen(true);
    };

    /** Step 2: admin confirmed payment — call backend validate */
    const handleConfirmPayment = async () => {
        if (!adhesionToValidate) return;

        setActionLoading(true);
        try {
            const response = await adhesionsService.validate(adhesionToValidate.id);
            const { code, adhesion } = response.data;

            // Close payment modal
            setIsPaymentModalOpen(false);
            setAdhesionToValidate(null);

            // Store all credentials from the API response (avoids "undefined undefined")
            setGeneratedCredentials({
                code,
                prenom: adhesion.prenom,
                nom: adhesion.nom,
                email: adhesion.email,
                numeroMembre: adhesion.numeroMembre || '',
                adhesionId: adhesion.id,
            });
            setEmailSent(false);

            fetchAdhesions();
            fetchStats();
        } catch (error: any) {
            // eslint-disable-next-line
            alert(error.response?.data?.message || 'Erreur lors de la validation du paiement');
        } finally {
            setActionLoading(false);
        }
    };

    /** Step 3: send professional email with credentials */
    const handleSendEmail = async () => {
        if (!generatedCredentials) return;

        setActionLoading(true);
        try {
            await adhesionsService.sendCredentials(
                generatedCredentials.adhesionId,
                generatedCredentials.code,
            );
            setEmailSent(true);
        } catch (error: any) {
            console.error('Error sending email:', error);
            alert(error.response?.data?.message || 'Erreur lors de l\'envoi de l\'email');
        } finally {
            setActionLoading(false);
        }
    };

    const handleExportExcel = async () => {
        setActionLoading(true);
        try {
            const response = await adhesionsService.getAll(filterStatut, 1, 100000, searchTerm);
            const data = response.data;

            const rows = data.map(adhesion => {
                const dateAdhesion = new Date(adhesion.createdAt);
                const finAdhesion = new Date(dateAdhesion);
                finAdhesion.setFullYear(finAdhesion.getFullYear() + 1);

                let statutFr = '';
                switch (adhesion.statut) {
                    case 'approved': statutFr = 'Approuvé'; break;
                    case 'rejected': statutFr = 'Rejeté'; break;
                    case 'validated': statutFr = 'Validé'; break;
                    default: statutFr = 'En attente'; break;
                }

                return {
                    'N° Membre': adhesion.numeroMembre || '',
                    'Civilité': adhesion.civilite || '',
                    'Nom': adhesion.nom,
                    'Prénom': adhesion.prenom,
                    'Email': adhesion.email,
                    'Téléphone': adhesion.telephone,
                    'Date de naissance': new Date(adhesion.dateNaissance).toLocaleDateString(),
                    'Lieu de naissance': adhesion.lieuNaissance || '',
                    'Adresse personnelle': adhesion.adressePersonnelle || '',
                    'Adresse d\'exercice': adhesion.adresse || '',
                    'Wilaya': adhesion.wilaya || '',
                    'Secteur': adhesion.secteur || '',
                    'Fonction / Titre': adhesion.fonctionTitre || '',
                    'Établissement / Structure': adhesion.etablissementStructure || '',
                    'Statut': statutFr,
                    'A payé': adhesion.statut === 'validated' ? 'Oui' : 'Non',
                    'Date d\'adhésion': dateAdhesion.toLocaleDateString(),
                    'Fin d\'adhésion': finAdhesion.toLocaleDateString()
                };
            });

            const worksheet = XLSX.utils.json_to_sheet(rows);
            const workbook = XLSX.utils.book_new();
            XLSX.utils.book_append_sheet(workbook, worksheet, 'Adhérents');
            
            // Generate and download Excel file
            XLSX.writeFile(workbook, 'adherents.xlsx');
        } catch (error) {
            console.error('Error exporting data:', error);
            alert('Erreur lors de l\'exportation');
        } finally {
            setActionLoading(false);
        }
    };

    const openDetails = (adhesion: Adhesion) => {
        setSelectedAdhesion(adhesion);
        setIsModalOpen(true);
    };

    const getStatusBadge = (status: string) => {
        switch (status) {
            case 'approved':
                return <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-semibold rounded-full flex items-center"><CheckCircle className="w-3 h-3 mr-1" /> Approuvé</span>;
            case 'rejected':
                return <span className="px-2 py-1 bg-red-100 text-red-800 text-xs font-semibold rounded-full flex items-center"><XCircle className="w-3 h-3 mr-1" /> Rejeté</span>;
            case 'validated':
                return <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full flex items-center"><BadgeCheck className="w-3 h-3 mr-1" /> Validé</span>;
            default:
                return <span className="px-2 py-1 bg-yellow-100 text-yellow-800 text-xs font-semibold rounded-full flex items-center"><Filter className="w-3 h-3 mr-1" /> En attente</span>;
        }
    };

    return (
        <div className="p-6">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-2xl font-bold text-gray-900">Gestion des Adhésions</h1>
            </div>

            {/* Statistics Cards */}
            {stats && (
                <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-8">
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100">
                        <p className="text-gray-500 text-sm">Total Demandes</p>
                        <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 border-l-4 border-l-yellow-400">
                        <p className="text-gray-500 text-sm">En attente</p>
                        <p className="text-2xl font-bold text-yellow-600">{stats.pending}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 border-l-4 border-l-green-400">
                        <p className="text-gray-500 text-sm">Approuvées</p>
                        <p className="text-2xl font-bold text-green-600">{stats.approved}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 border-l-4 border-l-blue-400">
                        <p className="text-gray-500 text-sm">Validées</p>
                        <p className="text-2xl font-bold text-blue-600">{stats.validated ?? 0}</p>
                    </div>
                    <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-100 border-l-4 border-l-red-400">
                        <p className="text-gray-500 text-sm">Rejetées</p>
                        <p className="text-2xl font-bold text-red-600">{stats.rejected}</p>
                    </div>
                </div>
            )}

            {/* Filters and Search */}
            <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                {/* Search */}
                <div className="relative flex-1 max-w-lg">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Search className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                        type="text"
                        placeholder="Rechercher par nom, email, téléphone..."
                        className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-brand-primary focus:border-brand-primary sm:text-sm"
                        value={searchTerm}
                        onChange={(e) => {
                            setSearchTerm(e.target.value);
                            setPage(1);
                        }}
                    />
                </div>

                {/* Filter */}
                <div className="flex items-center space-x-4">
                    <span className="text-sm font-medium text-gray-700 whitespace-nowrap">Filtrer par statut:</span>
                    <select
                        className="border-gray-300 rounded-md text-sm focus:ring-brand-primary focus:border-brand-primary"
                        value={filterStatut}
                        onChange={(e) => {
                            setFilterStatut(e.target.value);
                            setPage(1);
                        }}
                    >
                        <option value="">Tous</option>
                        <option value="pending">En attente</option>
                        <option value="approved">Approuvé</option>
                        <option value="validated">Validé</option>
                        <option value="rejected">Rejeté</option>
                    </select>

                    <button
                        onClick={handleExportExcel}
                        disabled={actionLoading}
                        className="inline-flex items-center px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 transition-colors whitespace-nowrap"
                        title="Exporter la liste filtrée au format Excel/CSV"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        Exporter Excel
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="bg-white shadow-sm rounded-lg overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Demandeur</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Contact</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Profession</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Statut</th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date demande</th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {loading ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">Chargement...</td>
                                </tr>
                            ) : adhesions.length === 0 ? (
                                <tr>
                                    <td colSpan={6} className="px-6 py-4 text-center text-sm text-gray-500">Aucune demande trouvée</td>
                                </tr>
                            ) : (
                                adhesions.map((adhesion) => (
                                    <tr key={adhesion.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">{adhesion.prenom} {adhesion.nom}</div>
                                            <div className="text-sm text-gray-500">{new Date(adhesion.dateNaissance).toLocaleDateString()}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{adhesion.email}</div>
                                            <div className="text-sm text-gray-500">{adhesion.telephone}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm text-gray-900">{adhesion.secteur}</div>
                                            <div className="text-sm text-gray-500">{adhesion.wilaya}</div>
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            {getStatusBadge(adhesion.statut)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {new Date(adhesion.createdAt).toLocaleDateString()}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            {/* Validate payment button — only shown for 'approved' adhesions */}
                                            {adhesion.statut === 'approved' && (
                                                <button
                                                    onClick={() => openPaymentConfirmation(adhesion)}
                                                    className="text-green-600 hover:text-green-800 bg-green-50 hover:bg-green-100 p-2 rounded-full transition-colors mr-2"
                                                    title="Valider le paiement et créer l'accès membre"
                                                    disabled={actionLoading}
                                                >
                                                    <CreditCard className="w-4 h-4" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => openDetails(adhesion)}
                                                className="text-brand-primary hover:text-brand-secondary bg-blue-50 p-2 rounded-full transition-colors"
                                                title="Voir détails"
                                            >
                                                <Eye className="w-4 h-4" />
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
                    <button
                        onClick={() => setPage(p => Math.max(1, p - 1))}
                        disabled={page === 1}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
                    >
                        Précédent
                    </button>
                    <span className="text-sm text-gray-700">Page {page} sur {totalPages}</span>
                    <button
                        onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                        disabled={page === totalPages}
                        className="px-3 py-1 border border-gray-300 rounded-md text-sm disabled:opacity-50"
                    >
                        Suivant
                    </button>
                </div>
            </div>

            {/* ─── Details Modal ─── */}
            {isModalOpen && selectedAdhesion && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75" onClick={() => setIsModalOpen(false)}></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
                            <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                                <div className="sm:flex sm:items-start">
                                    <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                                        <div className="flex justify-between items-center mb-4">
                                            <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                                                Détails de la demande
                                            </h3>
                                            {getStatusBadge(selectedAdhesion.statut)}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mt-2">
                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Civilité</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.civilite}</p>
                                            </div>
                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Nom complet</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.prenom} {selectedAdhesion.nom}</p>
                                            </div>
                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Date de naissance</p>
                                                <p className="text-sm text-gray-900">{new Date(selectedAdhesion.dateNaissance).toLocaleDateString()}</p>
                                            </div>
                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Lieu de naissance</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.lieuNaissance}</p>
                                            </div>

                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Email</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.email}</p>
                                            </div>
                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Téléphone</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.telephone}</p>
                                            </div>

                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Secteur</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.secteur}</p>
                                            </div>
                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Wilaya</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.wilaya}</p>
                                            </div>

                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Fonction / Titre</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.fonctionTitre}</p>
                                            </div>
                                            <div className="col-span-2 sm:col-span-1">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Établissement / Structure</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.etablissementStructure}</p>
                                            </div>

                                            <div className="col-span-2">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Adresse personnelle</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.adressePersonnelle}</p>
                                            </div>

                                            <div className="col-span-2">
                                                <p className="text-xs text-gray-500 uppercase font-semibold">Adresse d'exercice</p>
                                                <p className="text-sm text-gray-900">{selectedAdhesion.adresse}</p>
                                            </div>

                                            {selectedAdhesion.numeroMembre && (
                                                <div className="col-span-2 bg-blue-50 p-2 rounded">
                                                    <p className="text-xs text-blue-700 uppercase font-semibold">Numéro de membre</p>
                                                    <p className="text-sm font-bold text-blue-900">{selectedAdhesion.numeroMembre}</p>
                                                </div>
                                            )}


                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                                {selectedAdhesion.statut === 'pending' && (
                                    <>
                                        <button
                                            type="button"
                                            disabled={actionLoading}
                                            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-brand-secondary text-base font-medium text-white hover:bg-brand-secondary-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-brand-secondary sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                            onClick={() => handleApprove(selectedAdhesion.id)}
                                        >
                                            <CheckCircle className="w-4 h-4 mr-1" /> Approuver le profil
                                        </button>
                                        <button
                                            type="button"
                                            disabled={actionLoading}
                                            className="mt-3 w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                                            onClick={() => handleReject(selectedAdhesion.id)}
                                        >
                                            <X className="w-4 h-4 mr-1" /> Rejeter
                                        </button>
                                    </>
                                )}
                                <button
                                    type="button"
                                    className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                                    onClick={() => setIsModalOpen(false)}
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Payment Confirmation Modal ─── */}
            {isPaymentModalOpen && adhesionToValidate && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
                        </div>
                        <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full z-10">
                            <div className="flex items-center mb-4">
                                <div className="flex-shrink-0 h-10 w-10 flex items-center justify-center rounded-full bg-green-100 mr-3">
                                    <CreditCard className="h-5 w-5 text-green-600" />
                                </div>
                                <h3 className="text-lg font-semibold text-gray-900">Confirmer la validation du paiement</h3>
                            </div>

                            <div className="bg-gray-50 rounded-md p-4 mb-5 border border-gray-200">
                                <p className="text-sm text-gray-600 mb-1">Adhérent :</p>
                                <p className="text-base font-bold text-gray-900">
                                    {adhesionToValidate.prenom} {adhesionToValidate.nom}
                                </p>
                                <p className="text-sm text-gray-500 mt-1">{adhesionToValidate.email}</p>
                                {adhesionToValidate.numeroMembre && (
                                    <p className="text-xs text-blue-700 mt-1 font-mono">N° {adhesionToValidate.numeroMembre}</p>
                                )}
                            </div>

                            <div className="bg-amber-50 border border-amber-200 rounded-md p-3 mb-5 flex items-start gap-2">
                                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                                <p className="text-xs text-amber-800">
                                    Cette action va <strong>créer le compte membre</strong> et générer un code d'accès.
                                    Confirmez uniquement si les <strong>frais d'adhésion annuelle ont bien été acquittés</strong>.
                                </p>
                            </div>

                            <div className="flex justify-end gap-3">
                                <button
                                    type="button"
                                    disabled={actionLoading}
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50"
                                    onClick={() => {
                                        setIsPaymentModalOpen(false);
                                        setAdhesionToValidate(null);
                                    }}
                                >
                                    Annuler
                                </button>
                                <button
                                    type="button"
                                    disabled={actionLoading}
                                    className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50"
                                    onClick={handleConfirmPayment}
                                >
                                    <CreditCard className="w-4 h-4 mr-2" />
                                    {actionLoading ? 'Validation en cours...' : 'Confirmer le paiement'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* ─── Generated Credentials Modal ─── */}
            {generatedCredentials && (
                <div className="fixed inset-0 z-50 overflow-y-auto">
                    <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
                        <div className="fixed inset-0 transition-opacity" aria-hidden="true">
                            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
                        </div>
                        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
                        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
                            {/* Header */}
                            <div className="bg-green-600 px-6 py-4">
                                <div className="flex items-center">
                                    <BadgeCheck className="h-6 w-6 text-white mr-2" />
                                    <h3 className="text-lg font-semibold text-white">Adhésion Validée</h3>
                                </div>
                            </div>

                            <div className="bg-white px-6 pt-5 pb-4">
                                {/* Success message */}
                                <p className="text-sm text-gray-600 mb-4">
                                    Le compte pour{' '}
                                    <strong className="text-gray-900">
                                        {generatedCredentials.prenom} {generatedCredentials.nom}
                                    </strong>{' '}
                                    a été créé avec succès.
                                </p>

                                {/* Credentials card */}
                                <div className="bg-gray-50 rounded-lg border border-gray-200 p-4 space-y-3">
                                    {/* Member number */}
                                    {generatedCredentials.numeroMembre && (
                                        <div>
                                            <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide mb-1">
                                                Numéro de membre
                                            </p>
                                            <div className="flex items-center gap-2">
                                                <KeyRound className="w-4 h-4 text-blue-500" />
                                                <span className="font-mono text-base font-bold text-blue-700 tracking-widest">
                                                    {generatedCredentials.numeroMembre}
                                                </span>
                                                <button
                                                    onClick={() => navigator.clipboard.writeText(generatedCredentials.numeroMembre)}
                                                    className="ml-1 p-1 text-gray-400 hover:text-gray-600 rounded"
                                                    title="Copier le numéro"
                                                >
                                                    <Copy className="h-4 w-4" />
                                                </button>
                                            </div>
                                        </div>
                                    )}

                                    {/* Identifiant */}
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide mb-1">
                                            Identifiant (email)
                                        </p>
                                        <p className="text-sm text-gray-800 font-medium">{generatedCredentials.email}</p>
                                    </div>

                                    {/* Code adhérent */}
                                    <div>
                                        <p className="text-xs text-gray-500 uppercase font-semibold tracking-wide mb-1">
                                            Code Adhérent (mot de passe temporaire)
                                        </p>
                                        <div className="flex items-center justify-between bg-white border border-gray-300 rounded-md px-3 py-2">
                                            <code className="text-lg font-mono font-bold text-brand-primary tracking-wider">
                                                {generatedCredentials.code}
                                            </code>
                                            <button
                                                onClick={() => navigator.clipboard.writeText(generatedCredentials.code)}
                                                className="ml-2 p-1 text-gray-400 hover:text-gray-600 rounded"
                                                title="Copier le code"
                                            >
                                                <Copy className="h-4 w-4" />
                                            </button>
                                        </div>
                                    </div>
                                </div>

                                <p className="mt-3 text-xs text-amber-700 bg-amber-50 p-2 rounded border border-amber-200">
                                    ⚠️ Ce code ne sera affiché qu'une seule fois. Veuillez l'envoyer par email à l'adhérent.
                                </p>

                                {/* Email send section */}
                                <div className="mt-4">
                                    {emailSent ? (
                                        <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-md px-4 py-3">
                                            <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                                            <p className="text-sm font-medium">
                                                Email professionnel envoyé avec succès à{' '}
                                                <strong>{generatedCredentials.prenom} {generatedCredentials.nom}</strong>.
                                            </p>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            disabled={actionLoading}
                                            onClick={handleSendEmail}
                                            className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
                                        >
                                            <Mail className="h-4 w-4 mr-2" />
                                            {actionLoading ? 'Envoi en cours...' : 'Envoyer l\'email professionnel à l\'adhérent'}
                                        </button>
                                    )}
                                </div>
                            </div>

                            {/* Footer */}
                            <div className="bg-gray-50 px-6 py-3 flex justify-end border-t border-gray-200">
                                <button
                                    type="button"
                                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
                                    onClick={() => {
                                        setGeneratedCredentials(null);
                                        setEmailSent(false);
                                    }}
                                >
                                    Fermer
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdhesionListPage;
